import { Action, configureStore, ThunkAction } from '@reduxjs/toolkit';
import { PersistConfig, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { apiCallBegan, ErrorResponse, SuccessResponse } from './api';
import api from './middleware/api';
import { logoutUser, refreshUserToken } from './middleware/auth';
import handleRequestErrors from './middleware/errorHandler';
import rootReducer from './reducer';

const persistKey = 'persist:root';

const shouldResetPersistRoot = () => {
  try {
    const persistData = localStorage.getItem(persistKey);
    if (!persistData) return false; 

    const parsedData = JSON.parse(persistData);

    if (parsedData.users) {
      const usersData = JSON.parse(parsedData.users);
      if (usersData.auth) {
        return false; 
      }
    }

    return true; 
  } catch (e) {
    return true; 
  }
};

if (shouldResetPersistRoot()) {
  localStorage.removeItem(persistKey);
}

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: 'root',
  storage,
  whitelist: ['users']
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export default function confStore() {
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: { warnAfter: 256 },
        serializableCheck: { warnAfter: 256 }
      }).concat(api, handleRequestErrors, refreshUserToken, logoutUser),
    devTools: process.env.NODE_ENV !== 'production'
  });
}

type DispatchSuccessExtension = (action: ReturnType<typeof apiCallBegan>) => Promise<SuccessResponse>;
type DispatchErrorExtension = (action: ReturnType<typeof apiCallBegan>) => Promise<ErrorResponse>;

export type AppDispatch = ReturnType<typeof confStore>['dispatch'] & DispatchSuccessExtension & DispatchErrorExtension;
export type AppState = ReturnType<typeof rootReducer>;

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, AppState, unknown, Action>;

